/**
 * Procedural sound effects via WebAudio — no audio assets, matching the
 * image-free ethos. All output is gated by the player's sound settings (§8.2).
 */
import { settings } from "../store/settings";

let ctx: AudioContext | null = null;
let bgmNodes: { osc: OscillatorNode[]; gain: GainNode } | null = null;

function ac(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Call once on first user gesture so the AudioContext can start (autoplay policy). */
export function unlockAudio(): void {
  ac();
}

function blip(freq: number, durMs: number, type: OscillatorType, vol: number, slideTo?: number): void {
  if (!settings.get("sfx")) return;
  const c = ac();
  if (!c) return;
  const t0 = c.currentTime;
  const dur = durMs / 1000;
  const osc = c.createOscillator();
  const gain = c.createGain();
  const level = vol * settings.get("sfxVolume");
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(level, t0 + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

export function sfxDrop(): void {
  blip(220, 90, "sine", 0.18, 160);
}

/** Merge sound; pitch rises with chain depth for escalating feedback. */
export function sfxMerge(chainLevel: number): void {
  const base = 330 * Math.pow(1.18, Math.min(chainLevel, 8));
  blip(base, 130, "triangle", 0.22, base * 1.5);
}

export function sfxClear(): void {
  // Little ascending arpeggio.
  [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => blip(f, 180, "triangle", 0.25), i * 90));
}

export function sfxFail(): void {
  blip(200, 320, "sawtooth", 0.2, 90);
}

export function sfxTap(): void {
  blip(440, 40, "square", 0.08);
}

/** Toggle a soft ambient drone for background music (§8.2). */
export function setBgm(on: boolean): void {
  const c = ac();
  if (!c) return;
  if (on && !bgmNodes) {
    const gain = c.createGain();
    gain.gain.value = 0.0001;
    gain.gain.linearRampToValueAtTime(0.04 * settings.get("bgmVolume"), c.currentTime + 2);
    const freqs = [110, 164.81, 220];
    const osc = freqs.map((f) => {
      const o = c.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      o.detune.value = (Math.sin(f) * 6) | 0;
      o.connect(gain);
      o.start();
      return o;
    });
    gain.connect(c.destination);
    bgmNodes = { osc, gain };
  } else if (!on && bgmNodes) {
    const { osc, gain } = bgmNodes;
    gain.gain.linearRampToValueAtTime(0.0001, c.currentTime + 0.6);
    osc.forEach((o) => o.stop(c.currentTime + 0.7));
    bgmNodes = null;
  }
}

export function setBgmVolume(v: number): void {
  if (bgmNodes && ctx) bgmNodes.gain.gain.setTargetAtTime(0.04 * v, ctx.currentTime, 0.1);
}
